import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Alert,
  Toolbar,
  AppBar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Fade,
  useTheme,
  alpha,
  Snackbar,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  ViewColumn as BoardIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { 
  DndContext, 
  DragOverlay, 
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  TouchSensor,
  MouseSensor,
  useSensor,
  useSensors,
  closestCorners,
  rectIntersection
} from '@dnd-kit/core';
import { 
  SortableContext, 
  verticalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../../supabaseClient';
import { TaskCard, TaskColumn, TaskModal, TaskEditor, CalendarView } from './components';

const SectionPaper = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  position: 'relative',
  borderRadius: 12,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '@keyframes slideInUp': {
    '0%': {
      transform: 'translateY(100%)',
      opacity: 0,
    },
    '100%': {
      transform: 'translateY(0)',
      opacity: 1,
    }
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`
  }
}));

const Tasks = () => {
  const theme = useTheme();
  const { taskId, companyId } = useParams();
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('board');
  const [tasks, setTasks] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskEditorOpen, setTaskEditorOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  const [activeId, setActiveId] = useState(null);
  const [timeFormat, setTimeFormat] = useState('24hour');
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [completedColumnCollapsed, setCompletedColumnCollapsed] = useState(true);
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');

  // Helper function to show snackbar
  const showSnackbar = (message, severity = 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Enhanced sensors configuration for unlimited dragging
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 3,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 100,
      tolerance: 8,
    },
  });

  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });

  const sensors = useSensors(
    mouseSensor,
    touchSensor,
    keyboardSensor
  );

  useEffect(() => {
    fetchInitialData();
  }, []);

    // Handle URL-based task editing
  useEffect(() => {
    if (taskId && tasks.length > 0 && currentUser && companyId) {
      const taskToEdit = tasks.find(task => task.id === taskId);
      if (taskToEdit) {
        setSelectedTask(taskToEdit);
        setModalMode('edit');
        setTaskEditorOpen(true);
      } else {
        // Task not found, redirect to main todo
        navigate(`/company/${companyId}/tasks`);
      }
    } else if (window.location.pathname.includes('/tasks/new') && currentUser && companyId) {
      setSelectedTask({ defaultColumn: 'today' });
      setModalMode('create');
      setTaskEditorOpen(true);
    } else if (!taskId && !window.location.pathname.includes('/tasks/new')) {
      // No task ID and not creating new task, ensure editor is closed
      setTaskEditorOpen(false);
      setSelectedTask(null);
    }
  }, [taskId, tasks, currentUser, navigate, companyId]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Get user profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('User profile not found');
      }

      setCurrentUser({ ...profile, id: user.id });

      // Fetch user preferences
      await fetchUserPreferences(user.id);

      // Fetch company users for collaboration
      await fetchCompanyUsers();

      // Fetch user's personal tasks
      await fetchTasks(user.id);

    } catch (err) {
      console.error('Error in fetchInitialData:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async (userId) => {
    try {
      
      // Get personal tasks (created by or assigned to user)
      const { data: personalTasks, error: personalError } = await supabase
        .from('tasks')
        .select(`
          *,
          collaborators:task_collaborators(
            id,
            user_id,
            status
          )
        `)
        .or(`created_by.eq.${userId},assigned_to.eq.${userId}`)
        .order('sort_order', { ascending: true });

      if (personalError) {
        console.error('Error fetching personal tasks:', personalError);
        throw personalError;
      }

      // Get collaborative task IDs where user is a collaborator
      const { data: collaboratorData, error: collaboratorError } = await supabase
        .from('task_collaborators')
        .select('task_id')
        .eq('user_id', userId)
        .eq('status', 'active');

      if (collaboratorError) {
        console.error('Error fetching collaborator data:', collaboratorError);
      }

      // Also check ALL task_collaborators for debugging
      const { data: allCollaborators } = await supabase
        .from('task_collaborators')
        .select('*');

      // Get collaborative tasks
      let collaborativeTasks = [];
      if (collaboratorData && collaboratorData.length > 0) {
        const taskIds = collaboratorData.map(tc => tc.task_id);
        
        const { data: collabTasks, error: collabError } = await supabase
          .from('tasks')
          .select(`
            *,
            collaborators:task_collaborators(
              id,
              user_id,
              status
            )
          `)
          .in('id', taskIds)
          .order('sort_order', { ascending: true });

        if (collabError) {
          console.error('Error fetching collaborative tasks:', collabError);
        } else {
          collaborativeTasks = collabTasks || [];
        }
      } 

      // Combine and deduplicate tasks
      const allTasks = [...(personalTasks || []), ...collaborativeTasks];
      const uniqueTasks = allTasks.filter((task, index, self) => 
        index === self.findIndex(t => t.id === task.id)
      );

      // Get all unique collaborator user IDs from all tasks
      const allCollaboratorIds = [...new Set(
        uniqueTasks
          .flatMap(task => task.collaborators || [])
          .map(c => c.user_id)
      )];

      // Fetch user details for collaborators if any exist
      let collaboratorDetails = {};
      if (allCollaboratorIds.length > 0) {
        try {
          const { data: usersData } = await supabase
            .from('profiles')
            .select('id, full_name, first_name, last_name')
            .in('id', allCollaboratorIds);

          // Get current user's company for email lookup
          const { data: { user } } = await supabase.auth.getUser();
          const { data: companyData } = await supabase
            .from('companies')
            .select('id')
            .or(`created_by.eq.${user.id},allowed_users.cs.{${user.id}}`)
            .single();

          let emailsData = [];
          if (companyData?.id) {
            const { data: emails } = await supabase.rpc('get_user_emails_for_company', {
              p_company_id: companyData.id
            });
            emailsData = emails || [];
          }

          allCollaboratorIds.forEach(userId => {
            const userProfile = usersData?.find(u => u.id === userId);
            const userEmail = emailsData?.find(e => e.user_id === userId);
            collaboratorDetails[userId] = {
              id: userId,
              full_name: userProfile?.full_name || userProfile?.first_name || userProfile?.last_name,
              email: userEmail?.email
            };
          });
        } catch (err) {
          console.warn('Error fetching collaborator details:', err);
        }
      }

      // Add collaborators to each task with user details
      const tasksWithCollaborators = uniqueTasks.map(task => ({
        ...task,
        collaborators: (task.collaborators || []).map(collab => ({
          ...collab,
          user: collaboratorDetails[collab.user_id] || { id: collab.user_id, email: 'Unknown' }
        }))
      }));

      // Sort by sort_order
      tasksWithCollaborators.sort((a, b) => a.sort_order - b.sort_order);

      setTasks(tasksWithCollaborators);
    } catch (err) {
      console.error('fetchTasks error:', err);
      setError(err.message);
    }
  };

  const fetchCompanyUsers = async () => {
    try {
      // Get current user's company through the companies table (allowed_users or created_by)
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get the company where the current user is creator or in allowed_users
      const { data: companyData } = await supabase
        .from('companies')
        .select('id, allowed_users, created_by')
        .or(`created_by.eq.${user.id},allowed_users.cs.{${user.id}}`)
        .single();

      if (!companyData) {
        setUsers([]);
        return;
      }

      // Get all user IDs from the company (creator + allowed users)
      const userIds = [...(companyData.allowed_users || []), companyData.created_by].filter(Boolean);
      
      if (userIds.length === 0) {
        setUsers([]);
        return;
      }

      // Get user profiles for all user IDs
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, first_name, last_name, phone, role, created_at, updated_at')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Get emails from auth.users using the security definer function
      const { data: emailsData, error: emailsError } = await supabase.rpc('get_user_emails_for_company', {
        p_company_id: companyData.id
      });

      if (emailsError) console.warn('Could not fetch emails:', emailsError);

      // Combine profile data with email data
      const enhancedUsers = (profilesData || []).map(profile => {
        const emailData = emailsData?.find(e => e.user_id === profile.id);
        
        return {
          ...profile,
          email: emailData?.email,
          last_sign_in_at: emailData?.last_sign_in_at
        };
      });

      setUsers(enhancedUsers);
    } catch (err) {
      console.error('Error fetching company users:', err);
    }
  };

  const fetchUserPreferences = async (userId) => {
    try {
      const { data: preferences, error: preferencesError } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (preferencesError && preferencesError.code !== 'PGRST116') {
        throw preferencesError;
      }

      if (preferences) {
        setTimeFormat(preferences.time_format);
      } else {
        // Create default preferences if none exist
        await createUserPreferences(userId);
      }
    } catch (err) {
      console.error('Error fetching user preferences:', err);
    }
  };

  const createUserPreferences = async (userId) => {
    try {
      const { error } = await supabase
        .from('user_preferences')
        .upsert([{
          user_id: userId,
          time_format: '12hour'
        }], {
          onConflict: 'user_id'
        });

      if (error) throw error;
      setTimeFormat('12hour');
    } catch (err) {
      console.error('Error creating user preferences:', err);
    }
  };

  const updateTimeFormat = async (newFormat) => {
    try {
      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: currentUser.id,
          time_format: newFormat
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;
      setTimeFormat(newFormat);
    } catch (err) {
      console.error('Error updating time format:', err);
    }
  };

  const filteredTasks = tasks.filter(task => {
    // Search filter
    if (searchQuery && !task.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !task.description?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    // Priority filter
    if (priorityFilter !== 'all' && task.priority !== priorityFilter) {
      return false;
    }

    // Status filter
    if (statusFilter === 'active' && task.status === 'completed') {
      return false;
    }
    if (statusFilter === 'completed' && task.status !== 'completed') {
      return false;
    }

    return true;
  });

  const groupedTasks = {
    today: filteredTasks.filter(task => task.column_type === 'today'),
    this_week: filteredTasks.filter(task => task.column_type === 'this_week'),
    this_month: filteredTasks.filter(task => task.column_type === 'this_month'),
    later: filteredTasks.filter(task => task.column_type === 'later'),
    completed: tasks.filter(task => task.column_type === 'completed' || task.status === 'completed')
  };

  const handleCreateTask = async (columnTypeOrTaskData = 'today') => {
    // Check if this is a quick task object or just a column type
    if (typeof columnTypeOrTaskData === 'object' && columnTypeOrTaskData.title) {
      // This is a quick task creation - save directly without opening editor
      try {
        const { error } = await supabase
          .from('tasks')
          .insert([{
            ...columnTypeOrTaskData,
            created_by: currentUser.id,
            assigned_to: currentUser.id
          }]);
        
        if (error) throw error;
        fetchTasks(currentUser.id); // Refresh the tasks
      } catch (error) {
        console.error('Error creating quick task:', error);
      }
          } else if (companyId) {
        // This is regular task creation - navigate to new task URL
        navigate(`/company/${companyId}/tasks/new`);
      }
  };

  const handleEditTask = (task) => {
    if (companyId) {
      navigate(`/company/${companyId}/tasks/edit/${task.id}`);
    }
  };

  const handleSaveTask = async (taskData) => {
    try {
      if (modalMode === 'create') {
        const { error } = await supabase
          .from('tasks')
          .insert([{
            ...taskData,
            created_by: currentUser.id,
            assigned_to: currentUser.id
          }]);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('tasks')
          .update(taskData)
          .eq('id', taskData.id);
        
        if (error) throw error;
      }

      // Add a small delay to ensure collaborators are saved before refreshing
      setTimeout(async () => {
        await fetchTasks(currentUser.id);
      }, 500);
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteFileFromStorage = async (filePath) => {
    try {
      const { error } = await supabase.storage
        .from('to-do-files')
        .remove([filePath]);

      if (error) {
        console.error('Error deleting file from storage:', error);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const extractFilesFromMarkdown = (markdown) => {
    if (!markdown) return [];
    const fileRegex = /!\[.*?\]\(https:\/\/todo-file\/(.*?)\)/g;
    const files = [];
    let match;
    
    while ((match = fileRegex.exec(markdown)) !== null) {
      files.push(match[1]); // Extract the file path
    }
    
    return files;
  };

  const handleDeleteTask = async (taskId) => {
    try {
      // First, check if the current user is the owner of the task
      const taskToDelete = tasks.find(task => task.id === taskId);
      
      if (!taskToDelete) {
        showSnackbar('Task not found');
        return;
      }
      
      if (taskToDelete.created_by !== currentUser.id) {
        showSnackbar('Only the task owner can delete this task');
        return;
      }
      
      // Delete all files associated with this task
      if (taskToDelete.description) {
        const filesInTask = extractFilesFromMarkdown(taskToDelete.description);
        for (const filePath of filesInTask) {
          await deleteFileFromStorage(filePath);
        }
      }
      
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId);
      
      if (error) throw error;
      
      await fetchTasks(currentUser.id);
      showSnackbar('Task deleted successfully', 'success');
    } catch (err) {
      showSnackbar(`Failed to delete task: ${err.message}`);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      // Update task status and column locally first
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { ...task, status: 'completed', column_type: 'completed', completed_at: new Date().toISOString() }
            : task
        )
      );

      // Show completed column if it was collapsed
      if (completedColumnCollapsed) {
        setCompletedColumnCollapsed(false);
      }

      // Update the task directly - simpler approach
      const { error } = await supabase
        .from('tasks')
        .update({ 
          status: 'completed', 
          column_type: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('id', taskId);
      
      if (error) throw error;
      
      await fetchTasks(currentUser.id);
    } catch (err) {
      setError(err.message);
      // Revert on error
      await fetchTasks(currentUser.id);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    
    if (!over) return;
    
    const activeId = active.id;
    const overId = over.id;
    
    // Find what is being dragged and what it's over
    const activeTask = tasks.find(t => t.id === activeId);
    const overTask = tasks.find(t => t.id === overId);
    
    if (!activeTask) return;
    
    // If over a column container, allow drop
    const validColumns = ['today', 'this_week', 'this_month', 'later', 'completed'];
    if (validColumns.includes(overId)) {
      return;
    }
    
    // If over another task, always allow drop for reordering
    if (overTask) {
      return;
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    setActiveId(null);
    
    if (!over) return;
    
    const taskId = active.id;
    const overId = over.id;
    
    // Find the task being dragged
    const draggedTask = tasks.find(t => t.id === taskId);
    if (!draggedTask) return;
    
    // Determine the target column and position
    let newColumn = overId;
    const targetTask = tasks.find(t => t.id === overId);
    
    // If dropped on another task, get that task's column
    if (targetTask) {
      newColumn = targetTask.column_type;
    }
    
    // Validate column type
    const validColumns = ['today', 'this_week', 'this_month', 'later', 'completed'];
    if (!validColumns.includes(newColumn)) {
      return;
    }
    
    // Handle reordering within the same column
    if (draggedTask.column_type === newColumn && targetTask && targetTask.id !== taskId) {
      const columnTasks = tasks.filter(t => t.column_type === newColumn).sort((a, b) => a.sort_order - b.sort_order);
      const draggedIndex = columnTasks.findIndex(t => t.id === taskId);
      const targetIndex = columnTasks.findIndex(t => t.id === targetTask.id);
      
      if (draggedIndex === targetIndex) return; // No change needed
      
      // Reorder the tasks
      const reorderedTasks = arrayMove(columnTasks, draggedIndex, targetIndex);
      
      // Update local state immediately with new sort orders
      const newTasks = tasks.map(task => {
        if (task.column_type !== newColumn) return task;
        const newIndex = reorderedTasks.findIndex(t => t.id === task.id);
        return { ...task, sort_order: newIndex };
      });
      
      setTasks(newTasks);
      
      try {
        // Update all tasks in the column with new sort orders in parallel
        const updatePromises = reorderedTasks.map((task, index) => 
          supabase
            .from('tasks')
            .update({ sort_order: index })
            .eq('id', task.id)
        );
        
        const results = await Promise.all(updatePromises);
        
        // Check if any updates failed
        const failedUpdate = results.find(result => result.error);
        if (failedUpdate) {
          throw failedUpdate.error;
        }
        
        // Refresh from database to ensure consistency
        await fetchTasks(currentUser.id);
      } catch (err) {
        console.error('Error reordering task:', err);
        setError(err.message);
        // Revert on error
        await fetchTasks(currentUser.id);
      }
      
      return;
    }
    
    // Handle moving to a different column
    if (draggedTask.column_type !== newColumn) {
      // Update the task locally for immediate feedback
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { 
                ...task, 
                column_type: newColumn,
                status: newColumn === 'completed' ? 'completed' : 'todo',
                completed_at: newColumn === 'completed' ? new Date().toISOString() : null
              }
            : task
        )
      );

      try {
        // Calculate integer sort order for new column
        const targetColumnTasks = tasks.filter(t => t.column_type === newColumn && t.id !== taskId)
          .sort((a, b) => a.sort_order - b.sort_order);
        let newSortOrder;
        
        if (targetTask) {
          // Insert at specific position
          const targetTaskIndex = targetColumnTasks.findIndex(t => t.id === targetTask.id);
          newSortOrder = targetTaskIndex >= 0 ? targetTaskIndex : targetColumnTasks.length;
          
          // Update all tasks that need to shift
          const updatePromises = [];
          
          // Shift existing tasks in target column
          for (let i = newSortOrder; i < targetColumnTasks.length; i++) {
            updatePromises.push(
              supabase
                .from('tasks')
                .update({ sort_order: i + 1 })
                .eq('id', targetColumnTasks[i].id)
            );
          }
          
          // Move the dragged task
          updatePromises.push(
            supabase
              .from('tasks')
              .update({ 
                column_type: newColumn, 
                sort_order: newSortOrder,
                status: newColumn === 'completed' ? 'completed' : 'todo',
                completed_at: newColumn === 'completed' ? new Date().toISOString() : null
              })
              .eq('id', taskId)
          );
          
          const results = await Promise.all(updatePromises);
          const failedUpdate = results.find(result => result.error);
          if (failedUpdate) {
            throw failedUpdate.error;
          }
        } else {
          // Add to end of column
          newSortOrder = targetColumnTasks.length;
          
          const { error } = await supabase
            .from('tasks')
            .update({ 
              column_type: newColumn, 
              sort_order: newSortOrder,
              status: newColumn === 'completed' ? 'completed' : 'todo',
              completed_at: newColumn === 'completed' ? new Date().toISOString() : null
            })
            .eq('id', taskId);
          
          if (error) throw error;
        }
        
        // Refresh tasks from database to ensure consistency
        await fetchTasks(currentUser.id);
      } catch (err) {
        console.error('Error moving task:', err);
        setError(err.message);
      }
    }
  };

  const handleDateClick = (dateOrTaskData) => {
    // Check if this is a task object from quick add or just a date
    if (typeof dateOrTaskData === 'object' && dateOrTaskData.title) {
      // This is a quick task from calendar - pass it to handleCreateTask
      handleCreateTask(dateOrTaskData);
    } else {
      // This is just a date click - determine column and create task
      const date = dateOrTaskData;
      const today = new Date();
      const oneWeek = new Date(today);
      oneWeek.setDate(today.getDate() + 7);
      const oneMonth = new Date(today);
      oneMonth.setMonth(today.getMonth() + 1);

      let columnType = 'later';
      if (date <= today) {
        columnType = 'today';
      } else if (date <= oneWeek) {
        columnType = 'this_week';
      } else if (date <= oneMonth) {
        columnType = 'this_month';
      }

      handleCreateTask(columnType);
    }
  };

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha('#FF5722', 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha('#FF5722', 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha('#FF5722', 0.1),
                mb: 2
              }}>
                <AssignmentIcon sx={{ fontSize: 48, color: '#FF5722' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: 500,
              color: '#FF5722',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Tasks
            </Typography>
            <Typography variant="body2" sx={{
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Analyzing task management and scheduling data...
            </Typography>
            <Box sx={{
              width: '60%',
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#FF5722',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, borderRadius: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (taskEditorOpen) {
    return (
      <TaskEditor
        task={selectedTask}
        onSave={handleSaveTask}
        onCancel={() => companyId && navigate(`/company/${companyId}/tasks`)}
        users={users}
        currentUser={currentUser}
        mode={modalMode}
        timeFormat={timeFormat}
      />
    );
  }

  return (
    <Fade in timeout={800}>
      <Container 
        maxWidth="xl" 
        sx={{
          py: 3,
          pb: 8,
          px: { xs: 2, sm: 3 },
          width: '100%',
          maxWidth: '100vw !important'
        }}
      >
        {/* Enhanced Page Header */}


        {/* Filters and Controls */}
        <SectionPaper sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
              {/* Search */}
              <TextField
                size="small"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  width: 220,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'white',
                    height: 40,
                    fontSize: '0.9rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: alpha(theme.palette.grey[300], 0.8)
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />

              {/* Filters */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel sx={{ fontSize: '0.9rem' }}>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  label="Priority"
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  disableScrollLock={true}
                  disablePortal={false}
                  disableAutoFocus={true}
                  disableEnforceFocus={true}
                  MenuProps={{
                    disableScrollLock: true,
                    disablePortal: false,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    PaperProps: {
                      sx: {
                        position: 'absolute !important',
                        minWidth: 140,
                        contain: 'layout style paint',
                        zIndex: 9999,
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                      }
                    },
                    sx: {
                      zIndex: 9999
                    }
                  }}
                  sx={{
                    borderRadius: 3,
                    backgroundColor: 'white',
                    height: 40,
                    fontSize: '0.9rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: alpha(theme.palette.grey[300], 0.8)
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    }
                  }}
                >
                  <MenuItem value="all">All Priorities</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 110 }}>
                <InputLabel sx={{ fontSize: '0.9rem' }}>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  disableScrollLock={true}
                  disablePortal={false}
                  disableAutoFocus={true}
                  disableEnforceFocus={true}
                  MenuProps={{
                    disableScrollLock: true,
                    disablePortal: false,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    PaperProps: {
                      sx: {
                        position: 'absolute !important',
                        minWidth: 130,
                        contain: 'layout style paint',
                        zIndex: 9999,
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                      }
                    },
                    sx: {
                      zIndex: 9999
                    }
                  }}
                  sx={{
                    borderRadius: 3,
                    backgroundColor: 'white',
                    height: 40,
                    fontSize: '0.9rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: alpha(theme.palette.grey[300], 0.8)
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    }
                  }}
                >
                  <MenuItem value="all">All Tasks</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel sx={{ fontSize: '0.9rem' }}>Time</InputLabel>
                <Select
                  value={timeFormat}
                  label="Time"
                  onChange={(e) => updateTimeFormat(e.target.value)}
                  disableScrollLock={true}
                  disablePortal={false}
                  disableAutoFocus={true}
                  disableEnforceFocus={true}
                  MenuProps={{
                    disableScrollLock: true,
                    disablePortal: false,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    PaperProps: {
                      sx: {
                        position: 'absolute !important',
                        minWidth: 150,
                        contain: 'layout style paint',
                        zIndex: 9999,
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                      }
                    },
                    sx: {
                      zIndex: 9999
                    }
                  }}
                  sx={{
                    borderRadius: 3,
                    backgroundColor: 'white',
                    height: 40,
                    fontSize: '0.9rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: alpha(theme.palette.grey[300], 0.8)
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    }
                  }}
                >
                  <MenuItem value="12hour">12 Hour</MenuItem>
                  <MenuItem value="24hour">24 Hour</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* View Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.9rem' }}>
                View:
              </Typography>
              <Tabs
                value={currentView}
                onChange={(e, newValue) => setCurrentView(newValue)}
                sx={{ 
                  minHeight: 'auto',
                  '& .MuiTabs-root': {
                    minHeight: 40
                  },
                  '& .MuiTab-root': {
                    borderRadius: 3,
                    minHeight: 40,
                    minWidth: 'auto',
                    px: 3,
                    py: 1,
                    fontWeight: 500,
                    fontSize: '0.85rem',
                    textTransform: 'none',
                    border: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                    backgroundColor: 'white',
                    color: 'text.secondary',
                    mr: 1,
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      borderColor: theme.palette.primary.main
                    },

                  },
                  '& .MuiTabs-indicator': {
                    display: 'none'
                  }
                }}
              >
                <Tab
                  icon={<BoardIcon sx={{ fontSize: 18 }} />}
                  value="board"
                  label="Board"
                  iconPosition="start"
                />
                <Tab
                  icon={<CalendarIcon sx={{ fontSize: 18 }} />}
                  value="calendar"
                  label="Calendar"
                  iconPosition="start"
                />
              </Tabs>
            </Box>
          </Box>
        </SectionPaper>

        {/* Main Content */}
        <Box sx={{ flex: 1, overflow: 'visible' }}>
          {currentView === 'board' ? (
            <DndContext
              sensors={sensors}
              collisionDetection={rectIntersection}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              autoScroll={false}
            >
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(4, 1fr) minmax(70px, 300px)', 
                gap: 4, 
                height: '100%',
                overflow: 'visible',
                alignItems: 'start',
                px: 1
              }}>
                {/* Regular columns */}
                {Object.entries(groupedTasks).filter(([columnId]) => columnId !== 'completed').map(([columnId, columnTasks]) => (
                  <SortableContext
                    key={columnId}
                    items={columnTasks.map(task => task.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <TaskColumn
                      columnId={columnId}
                      tasks={columnTasks}
                      onTaskEdit={handleEditTask}
                      onTaskDelete={handleDeleteTask}
                      onTaskComplete={handleCompleteTask}
                      onTaskAssign={handleEditTask}
                      onAddTask={handleCreateTask}
                      users={users}
                      currentUser={currentUser}
                      timeFormat={timeFormat}
                      isCollapsed={false}
                      onToggleCollapse={null}
                    />
                  </SortableContext>
                ))}
                
                {/* Completed column - always present */}
                <SortableContext
                  key="completed"
                  items={groupedTasks.completed.map(task => task.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <TaskColumn
                    columnId="completed"
                    tasks={groupedTasks.completed}
                    onTaskEdit={handleEditTask}
                    onTaskDelete={handleDeleteTask}
                    onTaskComplete={handleCompleteTask}
                    onTaskAssign={handleEditTask}
                    onAddTask={handleCreateTask}
                    users={users}
                    currentUser={currentUser}
                    timeFormat={timeFormat}
                    isCollapsed={completedColumnCollapsed}
                    onToggleCollapse={() => setCompletedColumnCollapsed(!completedColumnCollapsed)}
                  />
                </SortableContext>
              </Box>

              <DragOverlay
                adjustScale={false}
                style={{ 
                  transformOrigin: '0 0',
                  zIndex: 9999
                }}
              >
                {activeId ? (
                  <Box sx={{ 
                    opacity: 0.95,
                    boxShadow: theme.shadows[8]
                  }}>
                    <TaskCard
                      task={tasks.find(task => task.id === activeId)}
                      users={users}
                      isDragging={true}
                    />
                  </Box>
                ) : null}
              </DragOverlay>
            </DndContext>
          ) : (
            <SectionPaper>
              <CalendarView
                tasks={filteredTasks}
                onTaskClick={handleEditTask}
                onDateClick={handleDateClick}
                timeFormat={timeFormat}
              />
            </SectionPaper>
          )}
        </Box>

        {/* Task Modal */}
        <TaskModal
          open={taskModalOpen}
          onClose={() => setTaskModalOpen(false)}
          task={selectedTask}
          onSave={handleSaveTask}
          users={users}
          currentUser={currentUser}
          mode={modalMode}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{
              width: '100%',
              borderRadius: 2,
              fontWeight: 500,
              color: 'black'
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Fade>
  );
};

export default Tasks;